export type ReloadSafety = 'safe' | 'blocked' | 'unknown';
export type ReloadBlockerState = ReloadSafety | (() => ReloadSafety);

export function getReloadActivitySafety(state: {
  draftDirty?: boolean;
  uploads?: number;
  queuedSends?: number;
  editorDirty?: boolean;
  editorSaving?: boolean;
  ready?: boolean;
}): ReloadSafety {
  if (
    state.draftDirty ||
    (state.uploads ?? 0) > 0 ||
    (state.queuedSends ?? 0) > 0 ||
    state.editorDirty ||
    state.editorSaving
  )
    return 'blocked';
  return state.ready === false ? 'unknown' : 'safe';
}

/** Coverage must name every owner on a route. An empty registry proves nothing. */
export function createReloadBlockerRegistry() {
  const blockers = new Map<
    string,
    {
      state: ReloadBlockerState;
      route?: string;
      nativeRestartState?: ReloadBlockerState;
    }
  >();
  const listeners = new Set<() => void>();
  let route: string | null = null;
  let required: readonly string[] | null = null;
  const notify = () => {
    for (const listener of listeners) listener();
  };
  const evaluateSafety = (nativeRestart: boolean): ReloadSafety => {
    let unknown = false;
    for (const blocker of blockers.values()) {
      // Native overrides may add restrictions; they never bypass a blocked web owner.
      const states =
        nativeRestart && blocker.nativeRestartState !== undefined
          ? [blocker.state, blocker.nativeRestartState]
          : [blocker.state];
      for (const candidate of states) {
        let state: ReloadSafety;
        try {
          state = typeof candidate === 'function' ? candidate() : candidate;
        } catch {
          state = 'unknown';
        }
        if (state === 'blocked') return 'blocked';
        if (state !== 'safe') unknown = true;
      }
    }
    const covered = (requirement: string) => {
      if (!requirement.endsWith(':*')) return blockers.has(requirement);
      const prefix = requirement.slice(0, -1);
      return [...blockers].some(
        ([id, owner]) => id.startsWith(prefix) && owner.route === route
      );
    };
    if (!route || required === null || required.some((id) => !covered(id)))
      return 'unknown';
    return unknown ? 'unknown' : 'safe';
  };
  return {
    getReloadSafety: () => evaluateSafety(false),
    getNativeReloadSafety: () => evaluateSafety(true),
    registerReloadBlocker(
      id: string,
      state: ReloadBlockerState,
      route?: string,
      nativeRestartState?: ReloadBlockerState
    ) {
      const owner = { state, route, nativeRestartState };
      blockers.set(id, owner);
      notify();
      const unregister = () => {
        if (blockers.get(id) !== owner) return;
        blockers.delete(id);
        notify();
      };
      return Object.assign(unregister, {
        set(
          state: ReloadBlockerState,
          nativeRestartState?: ReloadBlockerState
        ) {
          if (blockers.get(id) !== owner) return;
          owner.state = state;
          owner.nativeRestartState = nativeRestartState;
          notify();
        },
      });
    },
    setReloadBlocker(
      id: string,
      state: ReloadBlockerState,
      nativeRestartState?: ReloadBlockerState
    ) {
      const owner = blockers.get(id);
      if (!owner) return;
      owner.state = state;
      owner.nativeRestartState = nativeRestartState;
      notify();
    },
    setRouteCoverage(path: string, owners: readonly string[] | null) {
      route = path;
      required = owners;
      notify();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const reloadBlockerRegistry = createReloadBlockerRegistry();
export const {
  registerReloadBlocker,
  setReloadBlocker,
  getReloadSafety,
  getNativeReloadSafety,
} = reloadBlockerRegistry;
